import { useEffect, useState } from "react";
import { Factory, Printer, Users, X, BadgePercent, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, eur, formatApiError } from "@/lib/api";

function DiscountsEditor() {
  const [rows, setRows] = useState(null);
  const [saving, setSaving] = useState("");

  const load = () => api.get("/admin/discounts").then((res) => setRows(res.data)).catch(() => setRows([]));
  useEffect(() => {
    load();
  }, []);

  const setTier = (producer, idx, field, value) =>
    setRows((prev) =>
      prev.map((r) =>
        r.producer === producer
          ? { ...r, tiers: r.tiers.map((t, i) => (i === idx ? { ...t, [field]: value } : t)) }
          : r,
      ),
    );
  const addTier = (producer) =>
    setRows((prev) => prev.map((r) => (r.producer === producer ? { ...r, tiers: [...r.tiers, { threshold: "", rate: "" }] } : r)));
  const removeTier = (producer, idx) =>
    setRows((prev) => prev.map((r) => (r.producer === producer ? { ...r, tiers: r.tiers.filter((_, i) => i !== idx) } : r)));

  const save = async (producer, tiers) => {
    setSaving(producer);
    try {
      const cleaned = tiers.filter((t) => t.threshold !== "" && t.rate !== "");
      await api.put(`/admin/discounts/${encodeURIComponent(producer)}`, { tiers: cleaned });
      toast.success(cleaned.length ? `Remises ${producer} enregistrées` : `Aucune remise pour ${producer}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving("");
    }
  };

  return (
    <section data-testid="discounts-editor">
      <h2 className="font-display text-2xl font-semibold text-[#1C1917] flex items-center gap-2.5">
        <BadgePercent size={20} className="text-[#6B1226]" /> Remises panachées par fournisseur
      </h2>
      <p className="text-xs text-[#78716C] mt-1.5 leading-relaxed">
        Définissez les paliers (seuil en bouteilles → remise en € par bouteille). Aucun palier = pas de remise pour ce fournisseur.
      </p>
      {rows === null ? (
        <p className="mt-4 text-sm text-[#57534E] italic">Chargement…</p>
      ) : (
        <div className="mt-4 space-y-4">
          {rows.map((r) => (
            <div key={r.producer} className="bg-white rounded-2xl border border-[#6B1226]/10 p-5" data-testid="discount-producer-card">
              <p className="font-display text-lg font-semibold text-[#1C1917]">
                {r.producer} <span className="font-eyebrow text-[0.55rem] tracking-[0.25em] uppercase text-[#C5A059]">(viticulteur)</span>
              </p>
              <div className="mt-3 space-y-2">
                {r.tiers.map((t, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid="discount-tier-row">
                    <span className="text-xs text-[#57534E] shrink-0">dès</span>
                    <input
                      type="number"
                      min="1"
                      value={t.threshold}
                      onChange={(e) => setTier(r.producer, i, "threshold", e.target.value)}
                      className="w-20 text-center bg-[#F4EFE6] border border-[#6B1226]/15 rounded-lg px-2 py-2 text-sm outline-none focus:border-[#6B1226]/50"
                      data-testid="discount-tier-threshold"
                    />
                    <span className="text-xs text-[#57534E] shrink-0">bt →</span>
                    <input
                      type="number"
                      step="0.05"
                      min="0"
                      value={t.rate}
                      onChange={(e) => setTier(r.producer, i, "rate", e.target.value)}
                      className="w-20 text-center bg-[#F4EFE6] border border-[#6B1226]/15 rounded-lg px-2 py-2 text-sm outline-none focus:border-[#6B1226]/50"
                      data-testid="discount-tier-rate"
                    />
                    <span className="text-xs text-[#57534E] shrink-0">€/bt</span>
                    <button onClick={() => removeTier(r.producer, i)} className="p-1.5 text-stone-400 hover:text-[#6B1226] transition-colors" data-testid="discount-tier-remove" aria-label="Supprimer le palier">
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => addTier(r.producer)}
                  className="inline-flex items-center gap-1.5 rounded-full border border-[#6B1226]/25 text-[#6B1226] px-4 py-2 text-xs font-semibold hover:bg-[#6B1226]/5 transition-colors"
                  data-testid="discount-tier-add"
                >
                  <Plus size={13} /> Palier
                </button>
                <button
                  onClick={() => save(r.producer, r.tiers)}
                  disabled={saving === r.producer}
                  className="inline-flex items-center gap-1.5 rounded-full bg-[#6B1226] text-[#FAF6F0] px-4 py-2 text-xs font-bold hover:bg-[#580E1D] transition-colors disabled:opacity-60"
                  data-testid="discount-save"
                >
                  {saving === r.producer ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SupplierSheet({ producer, onClose }) {
  const [extras, setExtras] = useState({});

  const lines = producer.wines.map((w) => {
    const extra = parseInt(extras[w.wine_id] || "0", 10) || 0;
    return { ...w, extra, total: w.qty + extra };
  });
  const totalQty = lines.reduce((s, l) => s + l.total, 0);
  const today = new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="fixed inset-0 z-[65] bg-[#1C1917]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="supplier-sheet-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto no-scrollbar" data-lenis-prevent>
        <div id="supplier-sheet" className="printable-area p-6 sm:p-8">
          <div className="flex items-start justify-between no-print">
            <p className="eyebrow">Feuille à imprimer pour le fournisseur</p>
            <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#6B1226]/10 text-[#57534E]" data-testid="supplier-sheet-close" aria-label="Fermer">
              <X size={18} />
            </button>
          </div>

          <div className="text-center border-b-2 border-[#6B1226] pb-4 mt-2">
            <p className="font-display text-2xl font-bold text-[#1C1917] tracking-wide">COMMANDE FOURNISSEUR</p>
            <p className="text-sm text-[#57534E] mt-1">
              Le Bistroquet — Belfort · SIREN 492 701 313 · le {today}
            </p>
            <p className="font-display text-lg font-semibold text-[#6B1226] mt-2">{producer.producer}</p>
          </div>

          <table className="w-full mt-4 text-sm">
            <thead>
              <tr className="text-left text-[0.6rem] uppercase tracking-widest text-[#8C6D23] border-b border-[#6B1226]/20">
                <th className="py-2 pr-2">Produit</th>
                <th className="py-2 pr-2 text-center">Commandes clients</th>
                <th className="py-2 pr-2 text-center no-print">+ Stock</th>
                <th className="py-2 text-center">À commander</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l) => (
                <tr key={l.wine_id} className="border-b border-[#6B1226]/8" data-testid="supplier-line">
                  <td className="py-2.5 pr-2 text-[#1C1917]">{l.name}</td>
                  <td className="py-2.5 pr-2 text-center text-[#57534E]">{l.qty}</td>
                  <td className="py-2.5 pr-2 text-center no-print">
                    <input
                      type="number"
                      min="0"
                      value={extras[l.wine_id] || ""}
                      onChange={(e) => setExtras({ ...extras, [l.wine_id]: e.target.value })}
                      placeholder="0"
                      className="w-16 text-center bg-[#F4EFE6] border border-[#6B1226]/15 rounded-lg px-2 py-1.5 text-sm outline-none focus:border-[#6B1226]/50"
                      data-testid="supplier-extra-input"
                    />
                  </td>
                  <td className="py-2.5 text-center font-bold text-[#6B1226]">{l.total}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t-2 border-[#6B1226]">
                <td className="py-3 font-bold text-[#1C1917]">TOTAL BOUTEILLES</td>
                <td className="py-3 text-center text-[#57534E]">{lines.reduce((s, l) => s + l.qty, 0)}</td>
                <td className="py-3 text-center no-print text-[#57534E]">{lines.reduce((s, l) => s + l.extra, 0)}</td>
                <td className="py-3 text-center font-bold text-[#6B1226] text-base" data-testid="supplier-total">{totalQty}</td>
              </tr>
            </tfoot>
          </table>
          <p className="text-[0.65rem] text-[#A8A29E] mt-4 text-center print:block">
            Document interne Le Bistroquet — sans valeur contractuelle.
          </p>
        </div>

        <div className="px-6 pb-6 no-print">
          <button
            onClick={() => window.print()}
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#6B1226] text-[#FAF6F0] py-3.5 text-sm font-bold hover:bg-[#580E1D] transition-colors"
            data-testid="supplier-print-button"
          >
            <Printer size={16} /> Imprimer la feuille fournisseur
          </button>
        </div>
      </div>
    </div>
  );
}

export function AdminProducers() {
  const [data, setData] = useState(null);
  const [sheetFor, setSheetFor] = useState(null);

  useEffect(() => {
    api.get("/admin/producer-summary").then((res) => setData(res.data)).catch(() => setData({ producers: [], clients: [] }));
  }, []);

  if (data === null) {
    return <p className="mt-8 text-sm text-[#57534E] italic">Chargement…</p>;
  }

  const { producers, clients } = data;

  return (
    <div className="mt-8 space-y-10" data-testid="admin-producers">
      {/* Remises panachées par fournisseur */}
      <DiscountsEditor />

      {/* Tableau détaillé des clients */}
      <section>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-display text-2xl font-semibold text-[#1C1917] flex items-center gap-2.5">
            <Users size={20} className="text-[#6B1226]" /> Clients — commandes totales
          </h2>
          {clients.length > 0 && (
            <button
              onClick={() => {
                const el = document.getElementById("clients-sheet");
                el.classList.add("printable-area");
                window.print();
                setTimeout(() => el.classList.remove("printable-area"), 500);
              }}
              className="inline-flex items-center gap-1.5 rounded-full border border-[#6B1226]/30 text-[#6B1226] px-4 py-2 text-xs font-bold hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors"
              data-testid="clients-print-button"
            >
              <Printer size={13} /> Imprimer
            </button>
          )}
        </div>
        {clients.length === 0 ? (
          <p className="mt-4 text-sm text-[#78716C] italic" data-testid="clients-empty">Aucune commande client pour l'instant.</p>
        ) : (
          <div id="clients-sheet" className="mt-4 bg-white rounded-2xl border border-[#6B1226]/10 overflow-hidden" data-testid="clients-table">
            <div className="hidden print:block text-center pb-3 pt-2">
              <p className="font-bold text-lg">Le Bistroquet — Clients, commandes totales</p>
              <p className="text-xs text-stone-500">Édité le {new Date().toLocaleDateString("fr-FR")}</p>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[0.6rem] uppercase tracking-widest text-[#8C6D23] bg-[#F4EFE6]">
                  <th className="px-4 py-3">Client</th>
                  <th className="px-4 py-3 text-center">Commandes</th>
                  <th className="px-4 py-3 text-center">Bouteilles</th>
                  <th className="px-4 py-3 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {clients.map((c) => (
                  <tr key={c.email || c.name} className="border-t border-[#6B1226]/8" data-testid="client-line">
                    <td className="px-4 py-3">
                      <span className="block font-semibold text-[#1C1917]">{c.name}</span>
                      {c.email && <span className="block text-xs text-[#78716C]">{c.email}</span>}
                    </td>
                    <td className="px-4 py-3 text-center text-[#57534E]">{c.orders}</td>
                    <td className="px-4 py-3 text-center font-bold text-[#6B1226]">{c.total_qty}</td>
                    <td className="px-4 py-3 text-right price text-[#1C1917]">{eur(c.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Totaux par producteur + feuille imprimable */}
      <section>
        <h2 className="font-display text-2xl font-semibold text-[#1C1917] flex items-center gap-2.5">
          <Factory size={20} className="text-[#6B1226]" /> Besoins par producteur
        </h2>
        {producers.length === 0 ? (
          <p className="mt-4 text-sm text-[#78716C] italic" data-testid="admin-producers-empty">Les totaux par producteur apparaîtront dès les premières commandes.</p>
        ) : (
          <div className="mt-4 space-y-5" data-testid="admin-producers-list">
            {producers.map((p) => (
              <section key={p.producer} className="bg-white rounded-2xl border border-[#6B1226]/10 overflow-hidden" data-testid="producer-card">
                <div className="bg-[#6B1226] text-[#FAF6F0] px-5 py-4 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="font-display text-xl font-semibold">
                    {p.producer} <span className="font-eyebrow text-[0.55rem] tracking-[0.25em] uppercase text-[#E8D8B0]">(viticulteur)</span>
                  </h3>
                  <p className="text-sm" data-testid="producer-total">
                    <span className="font-bold">{p.total_qty} bouteille{p.total_qty > 1 ? "s" : ""}</span>
                    <span className="text-[#E8D8B0]/80"> · {eur(p.total_amount)}</span>
                  </p>
                </div>
                <ul>
                  {p.wines.map((w) => (
                    <li key={w.wine_id} className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#6B1226]/8 last:border-0 text-sm" data-testid="producer-wine-line">
                      <span className="font-semibold text-[#6B1226] w-16 shrink-0">{w.qty} bt</span>
                      <span className="flex-1 min-w-0 text-[#1C1917]">{w.name}</span>
                      <span className="price text-[#57534E] shrink-0">{eur(w.amount)}</span>
                    </li>
                  ))}
                </ul>
                <div className="px-5 py-4 bg-[#F4EFE6]">
                  <button
                    onClick={() => setSheetFor(p)}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-full border border-[#6B1226]/30 text-[#6B1226] py-2.5 text-xs font-bold hover:bg-[#6B1226] hover:text-[#FAF6F0] transition-colors"
                    data-testid="producer-sheet-button"
                  >
                    <Printer size={14} /> Préparer la feuille fournisseur (ajouter du stock, imprimer)
                  </button>
                </div>
              </section>
            ))}
          </div>
        )}
      </section>

      {sheetFor && <SupplierSheet producer={sheetFor} onClose={() => setSheetFor(null)} />}
    </div>
  );
}
